import React, { useState, useEffect } from "react";
import {
    Card, CardBody, Container, Spinner, Row, Col, Input, InputGroup
} from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { H3 } from "../../AbstractElements";
import { MapPin, Battery, Calendar } from 'react-feather';

interface LocationReportData {
    id: number;
    bystander_id: number;
    booking_id: number;
    latitude: number;
    longitude: number;
    battery_level: number;
    remarks: string;
    created_at: string;
    bystander?: {
        id: number;
        name: string;
    };
    booking?: {
        id: number;
        status: string;
        date: string;
        address: string;
        user_id: number;
        user?: {
            id: number;
            name: string;
        }
    };
}

export default function BystanderLocationLogs() {
    const [data, setData] = useState<LocationReportData[]>([]);
    const [loading, setLoading] = useState(true);

    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchData();
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response: any = await apiPost(`bystander/location_reports`, { search: searchTerm });
            if (response.status) {
                setData(response.data || []);
            } else {
                toast.error("Failed to load location logs");
            }
        } catch (error) {
            toast.error("Failed to load location logs");
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            name: "Sl.No",
            selector: (row: LocationReportData, index?: number) => (index !== undefined ? index + 1 : 0),
            cell: (row: LocationReportData, index: number) => <span>{index + 1}</span>,
            sortable: true,
            width: "100px",
        },
        {
            name: <div><span className="fw-bold">Bystander</span> Name</div>,
            selector: (row: LocationReportData) => row.bystander?.name || "Unknown",
            sortable: true,
            cell: (row: LocationReportData) => (
                <div className="d-flex flex-column">
                    <span className="fw-bold text-dark">{row.bystander?.name || "Unknown"}</span>
                </div>
            )
        },
        {
            name: <div><span className="fw-bold">Lat</span> Lng</div>,
            selector: (row: LocationReportData) => `${row.latitude}, ${row.longitude}`,
            cell: (row: LocationReportData) => (
                <div className="d-flex flex-column">
                    <span className="small fw-bold">Lat: {Number(row.latitude || 0).toFixed(6)}</span>
                    <span className="small fw-bold">Lng: {Number(row.longitude || 0).toFixed(6)}</span>
                </div>
            )
        },
        {
            name: "Date and Time",
            selector: (row: LocationReportData) => row.created_at,
            sortable: true,
            cell: (row: LocationReportData) => (
                <div className="d-flex flex-column">
                    <span className="fw-bold">{new Date(row.created_at).toLocaleDateString()}</span>
                    <span className="small text-muted">{new Date(row.created_at).toLocaleTimeString()}</span>
                </div>
            )
        },
        {
            name: "Battery",
            selector: (row: LocationReportData) => row.battery_level,
            sortable: true,
            cell: (row: LocationReportData) => {
                let color = "text-success";
                if (row.battery_level < 20) color = "text-danger";
                else if (row.battery_level < 50) color = "text-warning";
                return (
                    <div className="d-flex align-items-center gap-1">
                        <Battery size={14} className={color} />
                        <span className={`fw-bold ${color}`}>{row.battery_level}%</span>
                    </div>
                );
            }
        },
        {
            name: "Remarks",
            selector: (row: LocationReportData) => row.remarks,
            cell: (row: LocationReportData) => (
                <span className="text-muted f-12">{row.remarks || "No remarks"}</span>
            )
        }
    ];


    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: { fontSize: '15px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '50px' },
        },
    };


    return (
        <div className="page-body">
            <ToastContainer />
            <Breadcrumbs pageTitle="Location Disable Logs" parent="Bystander Management" title="Location Disable Logs" />
            <Container fluid={true}>
                <Card>
                    <CardBody>

                        <Row className="mb-3 justify-content-end">
                            <Col md={4} lg={3}>
                                <InputGroup className="shadow-sm border-0">
                                    <Input
                                        placeholder="Search by bystander..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="form-control-sm"
                                        style={{ borderRadius: '8px' }}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col sm="12">
                                <Card className="shadow-sm border-0">


                                    <CardBody className="p-0">
                                        <div className="table-responsive">
                                            <DataTable
                                                columns={columns}
                                                data={data}
                                                progressPending={loading}
                                                progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                                noDataComponent={
                                                    <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "50vh" }}>
                                                        <Card className="shadow-sm border-0 text-center" style={{ maxWidth: "320px", width: "100%" }}>
                                                            <CardBody>
                                                                <h6 className="fw-semibold mb-1">No Location Logs</h6>
                                                                <small className="text-muted">No instances of disabled location tracking found.</small>
                                                            </CardBody>
                                                        </Card>
                                                    </div>
                                                }
                                                pagination
                                                customStyles={customStyles}
                                                highlightOnHover
                                                pointerOnHover
                                            />
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
}
